import styled from '@emotion/styled';
import { alpha, Typography } from '@mui/material';
import { Utils } from 'jssip';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MicroMutedIcon, VideoRecorderOffIcon } from 'assets/svg';
import { sendAnalyticsError } from 'features/analytics/helpers';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { isMicrophoneMutedSelector } from 'features/call/call-base/store';
import {
  callIdSelector,
  sipCallIdSelector,
} from 'features/call/call-base/store/selectors';
import { UserVideo } from 'features/local-video/components';
import { useLocalVideo, useTrackGuard } from 'features/local-video/hooks';
import { isLocalVideoEnabledSelector } from 'features/local-video/store';
import { useMediaStream } from 'features/media-streams/hooks';
import { getNetworkStats, isRtcConnectionActive } from 'features/sip/helpers';
import { useSipSession } from 'features/sip/hooks';
import { textEllipsis } from 'features/theme/commonCss';
import theme from 'features/theme/theme';
import { agentNumberSelector, userSelector } from 'features/user/store';
import {
  isLocalVideoPrivacyEnabledSelector,
  startLocalVideoPrivacyLoading,
  stopLocalVideoPrivacyLoading,
} from 'features/video-privacy/store';
import { selectCallPageStatus } from 'features/call/call-ui-state/store';

export const LocalVideo: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const rtcConnection = useSipSession();
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const selfViewStreamRef = useRef<MediaStream | null>(null);
  const [selfViewStream, setSelfViewStream] = useState<MediaStream | null>(
    null
  );
  const { getInterpVideoStream, getSelfViewStream } = useMediaStream();
  const { sendVideoTrackToRemote, sendPrivacyTrackToRemote } = useLocalVideo();

  const isLocalVideoPrivacyEnabled = useAppSelector(
    isLocalVideoPrivacyEnabledSelector
  );
  const isLocalVideoEnabled = useAppSelector(isLocalVideoEnabledSelector);
  const isUserMicrophoneMuted = useAppSelector(isMicrophoneMutedSelector);
  const agentNumber = useAppSelector(agentNumberSelector);
  const user = useAppSelector(userSelector);
  const callPageStatus = useAppSelector(selectCallPageStatus);
  const callId = useAppSelector(callIdSelector);
  const sipCallId = useAppSelector(sipCallIdSelector);

  // Acquire a self-view stream (cloned track) once and hold it for the component's lifetime.
  // The clone is independent of the managed INTERP_VIDEO track
  useEffect(() => {
    let mounted = true;

    if (selfViewStreamRef.current) return;

    (async () => {
      try {
        const videoStream = await getSelfViewStream();
        if (mounted) {
          selfViewStreamRef.current = videoStream;
          setSelfViewStream(videoStream);
        } else {
          Utils.closeMediaStream(videoStream);
        }
      } catch (error) {
        console.error('Failed to acquire self-view stream:', error);
        sendAnalyticsError({
          name: 'Self View Stream Error',
          event: { error: String(error), callId, sipCallId },
        });
      }
    })();

    return () => {
      mounted = false;
      if (selfViewStreamRef.current) {
        Utils.closeMediaStream(selfViewStreamRef.current);
        selfViewStreamRef.current = null;
      }
    };
  }, [getSelfViewStream, callId, sipCallId]);

  const handleLocalVideo = useCallback(async () => {
    dispatch(startLocalVideoPrivacyLoading());

    try {
      if (isLocalVideoPrivacyEnabled) {
        mediaStreamRef.current = null;
        if (rtcConnection && isRtcConnectionActive(rtcConnection)) {
          await sendPrivacyTrackToRemote(rtcConnection);
        }
      } else {
        let streamToUse = mediaStreamRef.current;
        if (!streamToUse) {
          streamToUse = await getInterpVideoStream();
          mediaStreamRef.current = streamToUse;
        }
        if (rtcConnection && isRtcConnectionActive(rtcConnection)) {
          await sendVideoTrackToRemote(rtcConnection, streamToUse);
        }
      }
    } catch (error) {
      console.error('Failed to toggle local video:', error);
      sendAnalyticsError({
        name: 'Local Video Toggle Error',
        event: { error: String(error), callId, sipCallId },
      });
    } finally {
      dispatch(stopLocalVideoPrivacyLoading());
    }
  }, [
    dispatch,
    isLocalVideoPrivacyEnabled,
    rtcConnection,
    sendPrivacyTrackToRemote,
    getInterpVideoStream,
    sendVideoTrackToRemote,
    callId,
    sipCallId,
  ]);

  const handleStalled = useCallback(async () => {
    sendAnalyticsError({
      name: 'Black Screen Error',
      event: {
        user,
        callId,
        sipCallId,
        callPageStatus,
        networkStats: await getNetworkStats(rtcConnection),
      },
    });
  }, [callId, callPageStatus, rtcConnection, sipCallId, user]);

  useTrackGuard(rtcConnection, isLocalVideoPrivacyEnabled);

  useEffect(() => {
    handleLocalVideo();
  }, [handleLocalVideo]);

  return (
    <>
      {isLocalVideoEnabled && (
        <Container
          $isPrivacyEnabled={isLocalVideoPrivacyEnabled}
          data-testid='local-video'
        >
          {isLocalVideoPrivacyEnabled ? (
            <VideoRecorderOffIcon title='video-recorder-off-icon' />
          ) : (
            <UserVideo
              mediaStream={selfViewStream}
              handleStalled={handleStalled}
            />
          )}
          <UserLabel variant='caption'>
            {t('me')} ({agentNumber})
            {isUserMicrophoneMuted && <MutedIcon title='micro-muted' />}
          </UserLabel>
        </Container>
      )}
    </>
  );
};

const Container = styled('div')<{ $isPrivacyEnabled: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 266px;
  height: ${(props) => (props.$isPrivacyEnabled ? '200px' : 'auto')};
  /* TODO: get from theme when Doug updates designs */
  background-color: #222;
  border-radius: 8px;
`;

const UserLabel = styled(Typography)`
  max-width: 100%;
  position: absolute;
  display: flex;
  bottom: ${theme.spacing(8)};
  left: ${theme.spacing(7)};
  padding: ${theme.spacing(3, 4)};
  border-radius: 8px;
  background-color: ${alpha(theme.palette.common.black, 0.4)};
  color: ${theme.palette.common.white};
  ${textEllipsis}
`;

const MutedIcon = styled(MicroMutedIcon)`
  width: 16px;
  height: 16px;
  margin-left: ${theme.spacing(2)};
`;
